import React, { useEffect, useState } from "react";
import { Utils } from "helpers/utils";
import { DbScope } from "helpers/dbScope";
import { CacheConfig, CacheLimitDefs, CacheLimitName } from "helpers/cacheConfig";
import { ChatConverCache } from "helpers/chatConverCache";
import { DbUtils } from "helpers/dbUtils";
import { useRedux } from "hooks";
import { UserTypes } from "api";

// 20261022: per-device conversation cache limits + clear button (Settings > ระบบ และสิทธิ์อุปกรณ์)
// staffOnly rows: only for users who have WP/Task (Employee/ExternalUser, same rule as DisplaySetting's WP options).
// Customer/supplier usually see just their own room, so they only get "messages per room" (shortLabel).
const ROWS: Array<{ name: CacheLimitName; label: string; shortLabel?: string; staffOnly?: boolean }> = [
  { name: "chatRooms", label: "แชทลูกค้า/ผู้ขาย: จำนวนห้อง (รวมกัน)", staffOnly: true },
  { name: "chatMsgs", label: "แชทลูกค้า/ผู้ขาย: ข้อความต่อห้อง", shortLabel: "ข้อความต่อห้อง" },
  { name: "wpTopics", label: "WP/Task: จำนวนหัวข้อ (รวมกัน)", staffOnly: true },
  { name: "wpMsgs", label: "WP/Task: ข้อความต่อหัวข้อ", staffOnly: true },
];

const formatMb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1) + " MB";

const CacheSetting = () => {
  const { useAppSelector } = useRedux();
  const userType = useAppSelector((state: any) => state.Profile2.userType);
  const isStaff = userType === UserTypes.Employee || userType === UserTypes.ExternalUser; // customer/supplier: messages per room only
  const [limits, setLimits] = useState<Record<CacheLimitName, number>>(() =>
    ROWS.reduce((acc, r) => ({ ...acc, [r.name]: CacheConfig.get(r.name) }), {} as Record<CacheLimitName, number>));
  const [usage, setUsage] = useState<string>("");
  const [clearing, setClearing] = useState(false);
  // what is cached now (current user), shown next to the room/topic limits
  const [counts, setCounts] = useState<{ chat: number; wp: number; task: number } | null>(null);

  const refreshUsage = async () => {
    try {
      const est = await navigator.storage?.estimate?.();
      setUsage(est?.usage != null ? formatMb(est.usage) : "");
    } catch (e) {
      setUsage("");
    }
    try {
      const [chat, wpTk] = await Promise.all([ChatConverCache.count(), DbUtils.wp_topic_counts()]);
      setCounts({ chat, wp: wpTk.wp, task: wpTk.task });
    } catch (e) {
      setCounts(null);
    }
  };

  const countText = (name: CacheLimitName): string => {
    if (!counts) return "";
    if (name === "chatRooms") return `ใช้อยู่ ${counts.chat}`;
    if (name === "wpTopics") return `ใช้อยู่ ${counts.wp + counts.task} (WP ${counts.wp}, Task ${counts.task})`;
    return "";
  };

  useEffect(() => { refreshUsage(); }, []);

  const onChange = (name: CacheLimitName, value: number) => {
    CacheConfig.set(name, value);
    setLimits(prev => ({ ...prev, [name]: value }));
  };

  const onClear = () => {
    Utils.SweetConfirm("ล้างแคชข้อมูลทั้งหมดในอุปกรณ์นี้?<br/>ห้องแชทและหัวข้อจะโหลดจาก server ใหม่ในครั้งถัดไป")
      .then(async (result) => {
        if (!result.isConfirmed) return;
        setClearing(true);
        try {
          await DbScope.clearCaches();
          await refreshUsage();
          alert("ล้างแคชเรียบร้อย");
        } catch (e) {
          alert((e as any).message);
        } finally {
          setClearing(false);
        }
      })
      .catch((error) => window.alert((error as any).message));
  };

  return (
    <div>
      <p className="text-primary mb-1">แคชข้อมูลในอุปกรณ์นี้</p>
      <div className="text-muted mb-2" style={{ fontSize: "0.85em" }}>
        ค่ามากขึ้น เปิด{isStaff ? "ห้อง/หัวข้อเก่า" : "ห้อง"}ได้ทันที แต่ใช้พื้นที่มากขึ้นและบันทึกช้าลง (ตั้งค่าแยกตามอุปกรณ์)
      </div>

      {ROWS.filter(r => isStaff || !r.staffOnly).map(r => (
        <div key={r.name} className="d-flex justify-content-between align-items-center mb-1" style={{ gap: 8 }}>
          <label htmlFor={"cache_" + r.name} className="mb-0" style={{ flex: 1, minWidth: 0 }}>
            {isStaff || !r.shortLabel ? r.label : r.shortLabel}
            {countText(r.name) && <div className="text-muted" style={{ fontSize: "0.85em" }}>{countText(r.name)}</div>}
          </label>
          <select id={"cache_" + r.name} className="form-control" style={{ width: "auto", fontSize: "1.0em", padding: "4px" }}
            value={limits[r.name]} onChange={(e) => onChange(r.name, Number(e.target.value))}>
            {CacheLimitDefs[r.name].options.map(v => (
              <option key={v} value={v}>{v}{v === CacheLimitDefs[r.name].def ? " (ค่าเริ่มต้น)" : ""}</option>
            ))}
          </select>
        </div>
      ))}

      <div className="d-flex justify-content-between align-items-center mt-2" style={{ gap: 8 }}>
        <span className="text-muted" style={{ fontSize: "0.85em" }}>
          {usage ? "พื้นที่ที่แอปใช้ทั้งหมด: " + usage : ""}
        </span>
        <button type="button" className="btn btn-sm btn-outline-danger" disabled={clearing} onClick={onClear}>
          {clearing && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>}
          ล้างแคช
        </button>
      </div>
    </div>
  );
};

export default CacheSetting;
